import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Grid, Tooltip, IconButton, Paper } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable/';
import EditWarehouseDialog from 'components/popups/EditWarehouseDialog';
import WarehouseService from 'services/WarehouseService';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ShopContext } from 'Context/ShopContext';
import { UserContext } from 'Context/UserContext';
import { USER_PERMISSIONS } from 'const';
import { AlertMessagesContext } from 'react-alert-messages';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { WarehouseContext } from 'Context/WarehouseContext';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'const';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';

function Warehouses() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { shop } = useContext(ShopContext);
  const { user } = useContext(UserContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { getWarehouses: getWarehousesContext } = useContext(WarehouseContext);
  const history = useHistory();

  const [warehouseList, setWarehouseList] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [editData, setEditData] = useState(null);
  const [isEnableSearchRefreshButton, setIsEnableSearchRefreshButton] = useState(false);

  const userHasWarehousesWritePermission = user?.shop_permissions?.shop_permission === USER_PERMISSIONS.WRITE;

  const openEditWarehouseDialog = (warehouse) => {
    setEditData(warehouse);
  };

  const closeEditWarehouseDialog = () => {
    setEditData(null);
  };

  const getWarehouses = useCallback(
    async ({ search, useCache = true } = {}) => {
      setLoadingIndicator(true);
      try {
        if (search) {
          const response = await WarehouseService.getWarehouses({
            limit: 1000,
            search: search || undefined,
          });
          const data = response?.results || response || [];
          setWarehouseList(data);
        } else {
          const data = await getWarehousesContext({ useCache });
          setWarehouseList(data);
        }

        setIsEnableSearchRefreshButton(false);
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [postAlertMessage, getWarehousesContext]
  );

  useEffect(() => {
    if (!shop?.id || featureIsExpired(shop?.warehouse_validity)) return;
    getWarehouses();
  }, [shop?.id, shop?.warehouse_validity, getWarehouses]);

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroup}>
            {userHasWarehousesWritePermission && (
              <Tooltip title={tCommon('Edit Warehouse')} onClick={() => openEditWarehouseDialog(item)}>
                <IconButton size="small">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={tCommon('View Stock')}
              onClick={() => history.push(ROUTES.WAREHOUSE_STOCK.replace(':warehouseId', item.id))}
            >
              <IconButton size="small">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        );
      },
    },
  ];

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Warehouse')}
          <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
      </div>
      {shop && featureIsExpired(shop?.warehouse_validity) ? (
        <RestrictionInfo
          title={tCommon('Warehouse Support Feature not available')}
          content={tCommon('To get Warehouse support options, contact support team.')}
        />
      ) : (
        <>
          <div className={styles.changeable}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sm={3}>
                <h2 className={styles.subTitle}>{tCommon('Warehouses')}</h2>
              </Grid>
              <Grid item xs={12} sm={9}>
                <Grid container spacing={2} justify="flex-end">
                  <Grid item xs={12} sm={4}>
                    <div className={styles.searchSec}>
                      <input
                        type="text"
                        value={filterText}
                        onChange={(e) => {
                          setFilterText(e.target.value);
                          setIsEnableSearchRefreshButton(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            getWarehouses({ search: e.target.value.trim() });
                          }
                        }}
                        className={styles.searchInput}
                        placeholder={tCommon('Search Warehouses')}
                      />
                      <Tooltip title="Refresh">
                        <IconButton
                          size="small"
                          onClick={() => getWarehouses()}
                          disabled={!isEnableSearchRefreshButton}
                        >
                          <SyncIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>

          {(warehouseList && warehouseList.length > 0) || loadingIndicator ? (
            <Paper className={styles.tableWrapper}>
              <DataTable columns={headerData} rows={warehouseList} isLoading={loadingIndicator} />
            </Paper>
          ) : (
            !loadingIndicator && (
              <Info
                title={tOperations('No Warehouses Found')}
                content={tOperations('You have no warehouses to list.')}
              />
            )
          )}

          {editData && (
            <EditWarehouseDialog
              closeDialog={closeEditWarehouseDialog}
              editData={editData}
              reloadWarehouses={() => getWarehouses({ useCache: false })}
            />
          )}
        </>
      )}
    </div>
  );
}

export default withConsoleBase(Warehouses);
